import { enrichFieldsWithDbCol } from '../../../utils/metadata/fields';
import { formatSubmissionTerm } from '../../../utils/submissionTerm';

import type { LegacyFormSection } from '../../../types/record-form';
import type { ProcessedFieldMetadata } from '../../../types/metadata';

/**
 * Section layout for the submission popup.
 *
 * This is deliberately a client-side constant rather than `table_ui_component`
 * metadata. The popup is a bespoke design — status-driven disclosure, a rep
 * split editor embedded in one section, `auto` chips on derived columns — and
 * expressing that through Page Builder JSON would encode design decisions in
 * data that the Page Builder cannot then edit safely. The field *definitions*
 * still come from metadata; only the grouping lives here.
 *
 * Calculated columns are referenced by their `_calc` name, the same spelling
 * `table_ui_component` uses, so `enrichFieldsWithDbCol` resolves them.
 */
/**
 * Per-field layout overrides. The popup's labels are deliberately terser than
 * the metadata labels the grids use ("Factor" not "Factor Rate", "Comm %" not
 * "Commission Percent") — the design packs five fields to a row, so the labels
 * carry less. Everything omitted falls back to the field's own metadata.
 */
export interface SubmissionFieldSpec {
  field: string;
  label?: string;
  note?: string;
  requiredMark?: boolean;
  suffix?: string | ((values: Record<string, unknown>) => string);
  placeholder?: string;
  liveSave?: boolean;
}

export interface SubmissionSectionSpec {
  label: string;
  hint?: string;
  flat?: boolean;
  accent?: 'slate' | 'accent';
  fields: (string | SubmissionFieldSpec)[];
}

/** The term column counts payments, so its unit follows the structure. */
function termUnit(values: Record<string, unknown>): string {
  // An empty term still shows the plural unit, so the suffix is there as you type.
  const formatted = formatSubmissionTerm(values?.term || 2, values?.structure);
  const unit = formatted.split(' ').slice(1).join(' ');
  return unit ? ` ${unit}` : '';
}

export const SUBMISSION_SECTIONS: SubmissionSectionSpec[] = [
  {
    // Merchant and main rep live read-only in the dark header above; the body
    // carries only what gets edited on every deal.
    label: 'SUBMISSION',
    hint: 'used on every deal',
    fields: [
      { field: 'submission_date', label: 'Date' },
      { field: 'funder', label: 'Lender' },
      'status',
      // REG-902: Skyline or Region, set from the funder when the deal is sent.
      { field: 'sent_as_brand', label: 'Sent As' },
    ],
  },
  {
    label: 'APPROVAL',
    hint: 'terms',
    fields: [
      'amount',
      { field: 'factor_rate', label: 'Factor' },
      { field: 'term', suffix: termUnit },
      { field: 'payment_calc', label: 'Payment' },
      'structure',
      { field: 'commission_percent', label: 'Comm %' },
      { field: 'commission_calc', label: 'Comm $' },
      { field: 'payback_calc', label: 'Payback' },
      'deal_type',
    ],
  },
  {
    label: 'DECLINE',
    hint: 'why the deal stalled',
    fields: ['reason_for_decline', 'declined_date'],
  },
  {
    label: 'FUNDING DETAILS',
    accent: 'slate',
    fields: [
      { field: 'date_funded', label: 'Date Funded', requiredMark: true },
      'performance',
      { field: 'psf_dollar_amount', label: 'PSF $' },
      { field: 'psf_percentage_calc', label: 'PSF %' },
      { field: 'commission_received', label: 'Comm Received' },
      { field: 'renewal_date_calc', label: 'Renewal Date', note: '½ term' },
      { field: 'maturity_date_calc', label: 'Maturity', note: 'full term' },
      { field: 'gross_calc', label: 'Gross $', note: 'PSF + comm' },
      'syndication',
    ],
  },
  {
    // The splits themselves come from `rep_link`, injected as a section extra;
    // the editor's own footer shows the allocated total.
    label: 'REP COMMISSION',
    accent: 'accent',
    fields: [],
  },
  {
    label: 'AUDIT',
    hint: 'read-only',
    fields: [
      'status_change_date', 'approval_date', 'closed_on',
      'created_by', 'created_at', 'updated_by', 'updated_at',
    ],
  },
  {
    label: 'NOTES',
    flat: true,
    fields: [
      { field: 'note', placeholder: 'Add submission notes — funder feedback, merchant conversations, next steps…' },
    ],
  },
];

/** Statuses at or past which a section's fields start carrying real values. */
const APPROVED_ONWARD = ['approved', 'contract', 'funded'];
const FUNDED_ONLY = ['funded'];
/** A genuinely declined deal — the decline reason and date are the live fields. */
const DECLINED = ['declined', 'dead'];
/**
 * A submission held up waiting on paperwork. Deals are never declined for
 * missing documents, so this drives the SUBMISSION section's missing-documents
 * field rather than the DECLINE section. The needles cover every stored
 * spelling ('Missing documents', 'Awaiting Documents', 'Waiting On').
 */
const HELD_BY_DOCS = ['missing', 'await', 'waiting'];

function matches(status: string, needles: string[]): boolean {
  const value = (status || '').toLowerCase();
  return needles.some((needle) => value.includes(needle));
}

/**
 * Which sections open on load, from the deal's own state.
 *
 * A submitted deal has nothing in its funding fields, so opening them would be
 * eight empty inputs between the user and the fields they came for. Once the
 * deal reaches that stage the section is where the work happens, so it opens.
 */
export function defaultOpenSections(status: string, repLinkCount: number): Record<string, boolean> {
  const approved = matches(status, APPROVED_ONWARD);
  const funded = matches(status, FUNDED_ONLY);
  return {
    SUBMISSION: true,
    APPROVAL: approved,
    DECLINE: matches(status, DECLINED),
    'FUNDING DETAILS': funded,
    'REP COMMISSION': funded || repLinkCount > 0,
    AUDIT: false,
    NOTES: true,
  };
}

export interface SubmissionSectionState {
  status: string;
  repCount: number;
  /** Commission pool the rep splits divide up, from `commission_calc`. */
  commissionPool: number;
  /** Whether the record already holds a decline reason or date. */
  hasDeclineData: boolean;
  /** Whether the record already holds missing-documents values. */
  hasMissingDocs: boolean;
}

function money(value: number): string {
  return '$' + Math.round(value).toLocaleString('en-US');
}

/**
 * Header text that reflects the record rather than the schema. A funding
 * section on a submitted deal says it does not apply yet; on a funded deal it
 * says it is required. A rep section says how many reps share how much.
 */
function headerFor(spec: SubmissionSectionSpec, state: SubmissionSectionState): {
  hint?: string;
  badge?: LegacyFormSection['badge'];
} {
  if (spec.label === 'FUNDING DETAILS') {
    return { hint: matches(state.status, FUNDED_ONLY) ? 'required for funded deals' : 'funded deals only' };
  }
  if (spec.label === 'REP COMMISSION') {
    return {
      badge: {
        count: state.repCount,
        text: state.commissionPool ? `splitting ${money(state.commissionPool)}` : undefined,
      },
    };
  }
  return { hint: spec.hint };
}

/**
 * Turn the layout spec into the `sections` shape RecordDetailForm consumes,
 * dropping any field the current user's metadata does not expose.
 */
export function buildSubmissionSections(
  fieldData: ProcessedFieldMetadata | null | undefined,
  openMap: Record<string, boolean>,
  state: SubmissionSectionState,
): LegacyFormSection[] {
  if (!fieldData) return [];

  return SUBMISSION_SECTIONS.filter((spec) => {
    // Every other section is a stage a deal is heading toward, so a collapsed
    // header reads as a roadmap. A decline is an exception, and on a healthy
    // deal that header is just noise — the design has no such section at all.
    // It still appears once a deal is declined, and whenever the record already
    // holds decline data, so moving a declined deal back to active never hides
    // the reason it was declined. Missing documents are deliberately not part
    // of this: a deal held up by paperwork is not a declined deal.
    if (spec.label !== 'DECLINE') return true;
    return matches(state.status, DECLINED) || state.hasDeclineData;
  }).map((spec) => {
    // Missing documents ride the SUBMISSION section, appearing the moment the
    // status says the deal is waiting on paperwork — including an unsaved
    // status edit — and staying visible while the record still lists documents.
    // The field live-saves: which documents a deal is waiting on must survive
    // the modal being closed without pressing Save.
    const specFields = spec.label === 'SUBMISSION'
      && (matches(state.status, HELD_BY_DOCS) || state.hasMissingDocs)
      ? [...spec.fields, { field: 'missing_documents', liveSave: true }]
      : spec.fields;

    const enriched = (enrichFieldsWithDbCol(
      specFields.map((field) => (typeof field === 'string' ? { field } : field)),
      fieldData,
      'submission',
    ) || []) as LegacyFormSection['fields'];

    return {
      label: spec.label,
      ...headerFor(spec, state),
      flat: spec.flat,
      accent: spec.accent,
      defaultOpen: openMap[spec.label] !== false,
      fields: enriched.filter((field) => !!field.dbCol),
    };
  });
}
